import React, { useState, useEffect } from 'react';
import { driverApi } from '../../config/driverApi';
import { 
  Bell, 
  Trash2, 
  Check, 
  CheckCheck, 
  Clock, 
  AlertTriangle, 
  ClipboardList, 
  Info,
  CheckCircle,
  XCircle
} from 'lucide-react';

const DriverNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await driverApi.get('/notifications.php?action=notifications');
      if (res.data.success) {
        setNotifications(res.data.data);
        setUnreadCount(res.data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId = 0) => {
    try {
      const url = notificationId > 0 
        ? `/notifications.php?action=notifications&notification_id=${notificationId}`
        : `/notifications.php?action=notifications`;
      
      const res = await driverApi.put(url);
      if (res.data.success) {
        fetchNotifications();
      }
    } catch (err) {
      console.error('Failed to mark read:', err);
    }
  };

  const deleteNotification = async (notificationId = 0) => {
    if (notificationId === 0 && !window.confirm('Are you sure you want to clear all notifications?')) {
      return;
    }
    
    try {
      const url = notificationId > 0 
        ? `/notifications.php?action=notifications&notification_id=${notificationId}`
        : `/notifications.php?action=notifications`;
      
      const res = await driverApi.delete(url);
      if (res.data.success) {
        fetchNotifications();
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'new_order': return <ClipboardList size={18} color="#FF9800" />;
      case 'order_update': return <CheckCircle size={18} color="#4CAF50" />;
      case 'order_cancelled': return <XCircle size={18} color="#F44336" />;
      case 'delay_alert': return <AlertTriangle size={18} color="#FFC107" />;
      default: return <Info size={18} color="#2196F3" />;
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        <div style={{
          width: '36px',
          height: '36px',
          border: '3px solid #f0f0f0',
          borderTopColor: '#D4AF37',
          borderRadius: '50%',
          margin: '0 auto 16px',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <p>Loading notifications...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h2 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#1A1A1A',
            margin: 0,
          }}>
            Notifications
          </h2>
          <p style={{ color: '#666', fontSize: '0.85rem', margin: '4px 0 0' }}>
            You have {unreadCount} unread alert{unreadCount !== 1 ? 's' : ''}
          </p>
        </div>

        {notifications.length > 0 && (
          <div style={{ display: 'flex', gap: '8px' }}>
            {unreadCount > 0 && (
              <button
                onClick={() => markAsRead(0)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  color: '#333',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
            <button
              onClick={() => deleteNotification(0)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #ffcccc',
                backgroundColor: '#fff',
                color: '#D32F2F',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <Trash2 size={14} />
              Clear all
            </button>
          </div>
        )}
      </div>

      {notifications.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#fff',
          borderRadius: '16px',
          border: '1px solid #EAEDF1',
          color: '#999',
        }}>
          <Bell size={48} style={{ marginBottom: '16px', opacity: 0.3, color: '#D4AF37' }} />
          <p style={{ fontSize: '1rem', fontWeight: 600, color: '#333', marginBottom: '4px' }}>No notifications yet</p>
          <p style={{ fontSize: '0.85rem', margin: 0 }}>We'll alert you when orders or updates are assigned.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {notifications.map((notif) => (
            <div
              key={notif.notification_id}
              style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #EAEDF1',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                position: 'relative',
                borderLeft: notif.is_read ? '4px solid #e0e0e0' : '4px solid #D4AF37',
              }}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#FAFAFA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '2px',
                }}>
                  {getNotifIcon(notif.type)}
                </div>
                
                <div style={{ flex: 1, paddingRight: '24px' }}>
                  <h4 style={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: notif.is_read ? '#666' : '#1A1A1A',
                    margin: '0 0 4px',
                  }}>
                    {notif.title}
                  </h4>
                  <p style={{
                    fontSize: '0.85rem',
                    color: '#555',
                    margin: '0 0 8px',
                    lineHeight: 1.4,
                  }}>
                    {notif.message}
                  </p>
                  <span style={{
                    fontSize: '0.72rem',
                    color: '#999',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <Clock size={12} />
                    {new Date(notif.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>

              {/* Action Buttons on Card */}
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                display: 'flex',
                gap: '8px',
              }}>
                {!notif.is_read && (
                  <button
                    onClick={() => markAsRead(notif.notification_id)}
                    title="Mark as read"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#4CAF50',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Check size={16} />
                  </button>
                )}
                <button
                  onClick={() => deleteNotification(notif.notification_id)}
                  title="Delete"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#999',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DriverNotifications;
